import * as React from "react";
import Popover from "@mui/material/Popover";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";
import { useRole } from "../MangeContextProvide";
import FlagCategoryPicker, { FlagOption } from "../../common/FlagCategoryPicker";
import BulkUploadContactsDialog from "../BulkUploadContactsDialog";
import ContactFormDrawer from "./ContactFormDrawer";
import { deriveAllowedChannels } from "./contact.validation";
import {
  createContact,
  fetchContacts,
  fetchCountryCodes,
  fetchFlags,
  toggleContactStatus,
  updateContact,
} from "./contact.service";
import { Contact, CountryCode, DrawerState } from "./contact.types";
import SearchInput from "../../common/SearchInput";

const PAGE_SIZE = 10;

const emptyDrawerState: DrawerState = {
  id: null,
  salutation: "",
  name: "",
  organization: null,
  email: "",
  countryCode: "",
  whatappNumber: "",
  flags: [],
  allowedChannels: [],
  isActive: true,
};

export default function ManageContact() {
  const { globleCustomerId } = useRole() as any;

  const [rows, setRows] = React.useState<Contact[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [flags, setFlags] = React.useState<FlagOption[]>([]);
  const [countryCodes, setCountryCodes] = React.useState<CountryCode[]>([]);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerState, setDrawerState] = React.useState<DrawerState>(emptyDrawerState);
  const [flagPickerOpen, setFlagPickerOpen] = React.useState(false);

  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [countryDialog, setCountryDialog] = React.useState<string | null>(null);
  const [popover, setPopover] = React.useState<{ anchor: HTMLElement; content: React.ReactNode } | null>(null);



  const getContacts = React.useCallback(async (search: string) => {
    setLoading(true);
    try {
      const data = await fetchContacts(search);
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  React.useEffect(() => {
    getContacts(debouncedSearch);
  }, [debouncedSearch, getContacts]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  React.useEffect(() => {
    (async () => {
      setFlags(await fetchFlags());
      setCountryCodes(await fetchCountryCodes());
    })();
  }, []);

  const refetch = () => getContacts(debouncedSearch);

  const mappedRows = React.useMemo(
    () =>
      rows.map((row: any) => ({
        ...row,
        id: row?._id || row.id,
        flags: Array.isArray(row?.flags) ? row.flags : [],
        allowedChannels: row?.allowedChannels || [],
        isActive: row?.isActive !== false,
      })),
    [rows]
  );

  const pageCount = Math.max(1, Math.ceil(mappedRows.length / PAGE_SIZE));
  const pagedRows = mappedRows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  // ── Drawer ──
  const openAddDrawer = () => {
    setDrawerState(emptyDrawerState);
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: any) => {
    const org =
      typeof row.organizationName === "object"
        ? row.organizationName
        : row.masterContactOrganization || null;
    setDrawerState({
      id: row.id,
      salutation: row.salutation || "",
      name: row.name || "",
      organization: org,
      email: row.email || "",
      countryCode: row.countryCode || "",
      whatappNumber: row.whatappNumber || "",
      flags: row.flags || [],
      allowedChannels: row.allowedChannels || [],
      isActive: row.isActive !== false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const handleSaveContact = async () => {
    const payload = {
      salutation: drawerState.salutation,
      name: drawerState.name,
      email: drawerState.email || "",
      masterContactOrganization: drawerState.organization?._id?.toString() || "",
      whatappNumber: drawerState.whatappNumber,
      countryCode: drawerState.countryCode || "",
      flags: drawerState.flags.map((f) => f._id),
      allowedChannels: deriveAllowedChannels(drawerState),
      ...(drawerState.id ? { isActive: drawerState.isActive } : {}),
    };

    try {
      if (!drawerState.id) {
        const response = await createContact(payload);
        if (response.data.statusCode === 201) {
          toast.success(response.data.message);
          setCurrentPage(0);
          refetch();
          closeDrawer();
        }
      } else {
        const response = await updateContact(drawerState.id, payload);
        toast.success(response.data?.message || "Contact updated successfully");
        refetch();
        closeDrawer();
      }
    } catch (err: any) {
      const errors = err?.response?.data?.error;
      if (Array.isArray(errors)) errors.forEach((e: any) => toast.error(e.message));
      else if (typeof errors === "object" && errors) toast.error(Object.values(errors).join(", "));
      else if (typeof errors === "string") toast.error(errors);
      else toast.error("An error occurred while saving the contact.");
    }
  };

  const handleToggleStatus = async (row: any) => {
    try {
      const response = await toggleContactStatus(row.id, !row.isActive);
      refetch();
      if (response.status === 200) toast.success(response?.data?.message);
    } catch {
      toast.error("An error occurred while updating contact status.");
    }
  };

  const showFlagsPopover = (e: React.MouseEvent<HTMLElement>, flagList: FlagOption[]) => {
    setPopover({
      anchor: e.currentTarget,
      content: (
        <>
          <div className="ctpl-popover-title">🏷 All flags ({flagList.length})</div>
          <ul className="ctpl-popover-list">
            {flagList.map((f: any, i: number) => (
              <li key={f?._id || i}>{f?.category ? `${f.category}: ${f.name}` : f?.name}</li>
            ))}
          </ul>
        </>
      ),
    });
  };

  const renderFlags = (flagList: FlagOption[]) => {
    const shown = flagList.slice(0, 2);
    const extra = flagList.length - 2;
    return (
      <>
        {shown.map((f: any, i: number) => (
          <span className="ctpl-flag-chip" key={f?._id || i}>{f?.name}</span>
        ))}
        {extra > 0 && (
          <button type="button" className="ctpl-flag-more" onClick={(e) => showFlagsPopover(e, flagList)}>
            +{extra}
          </button>
        )}
      </>
    );
  };

  return (
    <>
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search contacts…"
          />

          <div className="ctpl-spacer" />

          <button className="ctpl-btn ctpl-btn-ghost" onClick={() => setBulkOpen(true)}>Bulk Upload</button>
          <button className="ctpl-btn ctpl-btn-primary" onClick={openAddDrawer}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Contact
          </button>
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>Name</th>
                <th style={{ width: "18%" }}>Organization</th>
                <th style={{ width: "24%" }}>Contact</th>
                <th style={{ width: "16%" }}>Flags</th>
                <th style={{ width: "12%", textAlign: "right", paddingRight: 22 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="ctpl-empty-cell" colSpan={6}>Loading contacts…</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td className="ctpl-empty-cell" colSpan={6}>No contacts found</td></tr>
              ) : (
                pagedRows.map((row: any) => {
                  const canManage = globleCustomerId === row?.customerId;
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="ctpl-tmpl-name">{row.salutation ? `${row.salutation}. ` : ""}{row.name}</div>
                        <div className="ctpl-tmpl-sub">{(row.allowedChannels || []).join(" · ") || "—"}</div>
                      </td>
                      <td>{typeof row.organizationName === "object" ? row.organizationName?.name : row.organizationName || "—"}</td>
                      <td>
                        <div style={{ fontSize: 12.5 }}>
                          {row.email && <div>✉️ {row.email}</div>}
                          {row.whatappNumber && <div>💬 +{row.countryCode} {row.whatappNumber}</div>}
                          {!row.email && !row.whatappNumber && "—"}
                        </div>
                      </td>
                      <td>{renderFlags(row.flags)}</td>
                      <td>
                        <div className="ctpl-row-actions">
                          {canManage ? (
                            <>
                              <button className="ctpl-action-circle edit" title={row.isActive ? "Edit" : "Activate to edit"} onClick={() => openEditDrawer(row)} disabled={!row.isActive}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className={`ctpl-switch${row.isActive ? " on" : ""}`}
                                title={row.isActive ? "Deactivate" : "Activate"}
                                onClick={() => handleToggleStatus(row)}
                              />
                            </>
                          ) : (
                            <span style={{ color: "var(--ctpl-ink-soft)", fontSize: 12 }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>{mappedRows.length} contact{mappedRows.length === 1 ? "" : "s"}</span>
          <div className="ctpl-pagination">
            <button className="ctpl-page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}>‹</button>
            <span>Page {currentPage + 1} of {pageCount}</span>
            <button className="ctpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}>›</button>
          </div>
        </div>
      </div>

      <ContactFormDrawer
        open={drawerOpen}
        state={drawerState}
        countryCodes={countryCodes}
        onChange={setDrawerState}
        onClose={closeDrawer}
        onSave={handleSaveContact}
        onEditFlags={() => setFlagPickerOpen(true)}
        onInactiveCountry={(msg) => setCountryDialog(msg)}
      />

      <FlagCategoryPicker
        open={flagPickerOpen}
        onClose={() => setFlagPickerOpen(false)}
        flags={flags}
        value={drawerState.flags}
        selectionMode="multi"
        categoryOrder={["Branch", "Job Type", "Mode"]}
        title="Tag Flags"
        onSave={(selected: any) => {
          setDrawerState((s) => ({ ...s, flags: selected }));
          setFlagPickerOpen(false);
        }}
      />

      <BulkUploadContactsDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCompleted={() => refetch()}
      />

      <Dialog open={!!countryDialog} onClose={() => setCountryDialog(null)}>
        <DialogTitle>Inactive Country Code</DialogTitle>
        <DialogContent>
          <DialogContentText>{countryDialog}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCountryDialog(null)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={!!popover}
        anchorEl={popover?.anchor}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "ctpl-popover-paper" }}
      >
        {popover?.content}
      </Popover>
    </>
  );
}